import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { ChevronDown, Building2, Search, Check, X } from "lucide-react";

// ── 프로젝트(고객사) 검색 유틸 ──
// 프로젝트가 28개+로 늘어나 네이티브 <select> 스크롤 탐색이 불편해짐(2026-08-05).
// 검색 입력이 있는 커스텀 콤보박스로 교체. 일반 부분일치 + 한글 초성 검색 지원.
const CHO = [
  "ㄱ", "ㄲ", "ㄴ", "ㄷ", "ㄸ", "ㄹ", "ㅁ", "ㅂ", "ㅃ", "ㅅ",
  "ㅆ", "ㅇ", "ㅈ", "ㅉ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ",
];

/** 문자열의 한글 음절을 초성으로 치환("삼성전자"→"ㅅㅅㅈㅈ"). 그 외 문자는 그대로. */
function toChosung(s: string): string {
  let out = "";
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if (code >= 0xac00 && code <= 0xd7a3) out += CHO[Math.floor((code - 0xac00) / 588)];
    else out += ch;
  }
  return out;
}

/** 검색 정규화: 소문자화 + 공백 제거 */
function norm(s: string): string {
  return (s || "").toLowerCase().replace(/\s+/g, "");
}

/** 질의가 초성으로만 이루어졌는지(ㄱ-ㅎ) — 초성 검색 모드 판단 */
function isChosungQuery(q: string): boolean {
  return q.length > 0 && [...q].every((ch) => CHO.includes(ch));
}

/** label 이 질의와 매칭되는가 — 부분일치 또는 초성 부분일치 */
function matches(label: string, q: string): boolean {
  if (!q) return true;
  const l = norm(label);
  if (l.includes(q)) return true;
  if (isChosungQuery(q) && toChosung(l).includes(q)) return true;
  return false;
}

interface FlatOption {
  value: string;
  label: string;
  shelf: string | null; // 그룹(책장) 이름. 구분 그룹 항목은 INTERNAL_SHELF
  exclusive?: boolean;  // 전용 스코프 — 목록에 '전용' 배지를 띄운다
}

/** 전체지식·전용 스코프를 묶는 가상 그룹 이름(책장이 아니라 UI 분류다). */
const INTERNAL_SHELF = "구분";

/**
 * 내메일 전용 검색 스코프(2026-09-30) — 서버가 켜져 있고(status.my_mail_scope) 본인이 메일을
 * 연동했을 때만 {value,label} 을 돌려준다. 그 외(기능 off·미연동·조회 실패)는 null → 항목 미노출.
 * 검색 범위·소유자 판정은 서버가 로그인 세션으로 하므로 여기서는 '보여줄지' 만 정한다.
 */
export function useMyMailScope(): { value: string; label: string } | null {
  const { data: st } = useQuery({
    queryKey: ["mailStatus"],
    queryFn: () => api.mailStatus().catch(() => null),
    staleTime: 5 * 60_000,
  });
  const scope = st?.enabled && st?.search_enabled ? st.my_mail_scope ?? null : null;
  const { data: acc } = useQuery({
    queryKey: ["mailAccount"],
    queryFn: () => api.mailAccount().catch(() => ({ account: null })),
    enabled: Boolean(scope),
  });
  return scope && acc?.account ? scope : null;
}

export default function CustomerSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { data } = useQuery({ queryKey: ["customers"], queryFn: api.customers });
  const myMail = useMyMailScope();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0); // 필터 결과(flat) 기준 키보드 포커스 인덱스
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // 전체 옵션 — 모든 항목이 그룹(shelf)에 속한다. 목록은 2단 구조로 보인다.
  //   구분            ← 그룹 헤더
  //     전체지식      ← value=""(기존 '내부지식' 동작: 고객사 필터 없이 전체 검색)
  //     서비스요청(SR) ← 배타 스코프. 서버가 exclusive=true 로 내려준다
  //     내메일        ← 메일 연동 사용자에게만(useMyMailScope). 본인 메일만 검색
  //   프로젝트…       ← 그룹 헤더(책장명)
  //     대웅제약 …
  // 배타 스코프는 서버에서 자기 책장(category)으로 오지만, 사용자에게는 고객사
  // 프로젝트가 아니라 '구분' 그룹의 한 갈래이므로 여기서 끌어올려 배치한다.
  const allOptions = useMemo<FlatOption[]>(() => {
    const opts: FlatOption[] = [
      { value: "", label: "전체지식", shelf: INTERNAL_SHELF },
    ];
    for (const g of data?.groups ?? [])
      for (const it of g.items)
        if (it.exclusive)
          opts.push({ value: it.value, label: it.label, shelf: INTERNAL_SHELF, exclusive: true });
    if (myMail)
      opts.push({ value: myMail.value, label: myMail.label, shelf: INTERNAL_SHELF, exclusive: true });
    for (const g of data?.groups ?? [])
      for (const it of g.items)
        if (!it.exclusive)
          opts.push({ value: it.value, label: it.label, shelf: g.shelf });
    return opts;
  }, [data, myMail]);

  // 검색창 안내용 — 구분 그룹(전체지식·전용 스코프)을 뺀 실제 프로젝트 수
  const projectCount = allOptions.filter((o) => o.shelf !== INTERNAL_SHELF).length;

  // 검색 필터 — 프로젝트명 또는 그룹(책꽂이)명 매칭
  const filtered = useMemo<FlatOption[]>(() => {
    const q = norm(query);
    if (!q) return allOptions;
    return allOptions.filter((o) => matches(o.label, q) || (o.shelf ? matches(o.shelf, q) : false));
  }, [allOptions, query]);

  // 현재 선택 표시 라벨
  const currentLabel = useMemo(() => {
    const found = allOptions.find((o) => o.value === value);
    return found ? found.label : value || "전체지식";
  }, [allOptions, value]);

  // 열 때: 검색 초기화 + 현재 선택으로 포커스 이동 + 입력창 포커스
  const openPanel = () => {
    setQuery("");
    const idx = allOptions.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx : 0);
    setOpen(true);
    // 렌더 후 포커스(모바일 키보드 포함)
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // 검색어 변경 시 첫 결과로 포커스 이동
  useEffect(() => {
    setActive(0);
  }, [query]);

  // 키보드 포커스 항목이 보이도록 스크롤
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  // 바깥 클릭 → 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const select = (v: string) => {
    setOpen(false);
    onChange(v);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = filtered[active];
      if (it) select(it.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  // 그룹 헤더 표시 여부: 직전 항목과 shelf 가 다를 때만
  const showShelfHeader = (idx: number) =>
    filtered[idx].shelf !== null && (idx === 0 || filtered[idx - 1].shelf !== filtered[idx].shelf);

  return (
    <div ref={rootRef} className="relative inline-block max-w-[60vw]">
      {/* 트리거 — 기존 select 외형 유지 */}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="flex max-w-[60vw] cursor-pointer items-center gap-2 rounded-full border border-border bg-card py-1.5 pl-3 pr-3 text-sm font-medium shadow-sm outline-none transition-colors hover:bg-muted focus:ring-2 focus:ring-primary/40"
        aria-label="프로젝트 선택"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="truncate">{currentLabel}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 w-[min(20rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-border bg-card shadow-xl">
          {/* 검색 입력 */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`프로젝트 검색 (${projectCount}개)`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              aria-label="프로젝트 검색"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  inputRef.current?.focus();
                }}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="검색어 지우기"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* 결과 목록 */}
          <ul ref={listRef} role="listbox" aria-label="프로젝트 목록" className="max-h-[50vh] overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                '{query}' 검색 결과가 없습니다.
              </li>
            )}
            {filtered.map((o, idx) => (
              <li key={o.value || "__internal__"}>
                {showShelfHeader(idx) && (
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold text-muted-foreground">
                    {o.shelf}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  data-idx={idx}
                  onClick={() => select(o.value)}
                  onPointerMove={() => setActive(idx)}
                  className={
                    // pl-6: 그룹 헤더(px-3) 아래 한 단 들여쓴다
                    "flex w-full items-center gap-2 py-2 pl-6 pr-3 text-left text-sm " +
                    (idx === active ? "bg-muted " : "") +
                    (o.value === value ? "font-semibold text-primary" : "")
                  }
                >
                  <span className="truncate">{o.label}</span>
                  {o.exclusive && (
                    <span
                      className="shrink-0 rounded-full border border-primary/40 px-1.5 py-0.5 text-[10px] font-medium text-primary"
                      title="이 범위 자료만 검색합니다(사내 공통자료 제외)"
                    >
                      전용
                    </span>
                  )}
                  {o.value === value && <Check className="ml-auto h-4 w-4 shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
